/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';
import { CREATE_LEAD } from '../../../queries/leads';
import { Course, Mutation, MutationAttachCourseToEmailArgs } from '../../../types/schema';
import { handleGraphqlError } from '../../../utils/error';

import RegisterPageTemplate from './template';
import { IFormInputs } from './template';
import useLocalStorage from '../../../hooks/useLocalStorage';
import rollbar from '../../../utils/rollbar';
import { trackPixelEvent } from '../../../utils/facebook-pixel';

const USER_DATA = 'userData';

export type UserData = {
  name: string;
  phone: string;
  email: string;
};

const RegisterPage: React.FC<Course> = (props) => {
  const [course, setCourse] = useState<Course>(props);
  const [captcha, setCaptcha] = useState<string | undefined | null>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState({ field: '', message: '' });
  const [userData, setUserData] = useLocalStorage<UserData | null>({
    key: USER_DATA,
    initialValue: null,
  });

  const captchaRef = useRef<ReCAPTCHA | null>(null);

  const router = useRouter();
  const { slug } = router.query;

  // Mutations
  const [createLead, { loading: createLoading }] = useMutation<
    Mutation,
    MutationAttachCourseToEmailArgs
  >(CREATE_LEAD);

  useEffect(() => {
    setCourse(props);
  }, [props]);

  useEffect(() => {
    if (success) {
      setError({
        field: '',
        message: '',
      });
      captchaRef.current?.reset();
      setCaptcha('');
    }
  }, [success]);

  const onSubmit = async (
    formData: IFormInputs,
    e: React.BaseSyntheticEvent<object, any, any> | undefined,
  ) => {
    e?.preventDefault();
    const userData = {
      email: formData.email,
      name: formData.name,
      phone: formData.tel,
    };

    setUserData(userData);

    if (!captcha) {
      return setError({
        field: 'captcha',
        message: 'Complete o CAPTCHA para enviar sua mensagem',
      });
    }
    const data = {
      ...userData,
      newsletter: formData.newsletter,
      courseSlug: slug as string,
      courseTitle: course.title as string,
      status: course.preSubscription ? 'pre_subscribed' : 'subscribed',
    };
    try {
      const result = await createLead({
        variables: data,
      });
      if (props.price && props.price > 0) {
        trackPixelEvent('InitiateCheckout');
      }

      localStorage.setItem(
        'SUBSCRIPTION_DATA',
        JSON.stringify({
          leadId: result.data?.attachCourseToEmail?.lead?.documentId,
          courseId: result.data?.attachCourseToEmail?.course?.documentId,
        }),
      );
      if (course.paymentUrl) {
        router.push(course.paymentUrl);
      } else {
        setSuccess(true);
      }
    } catch (error: any) {
      rollbar.error('Error creating Lead', { error, data });
      const result = handleGraphqlError(error);
      if (result && result.formError) {
        toast.error(result?.formError[0].message, {
          position: 'top-right',
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        setError(error.message);
      }
      setSuccess(false);
    }
  };

  return (
    <RegisterPageTemplate
      course={{ ...course, slug: slug as string }}
      bannerTitle={course.preSubscription ? 'Pré-inscrição' : 'Inscrição'}
      captchaRef={captchaRef}
      setCaptcha={setCaptcha}
      sitekey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA! as string}
      onSubmit={onSubmit}
      error={error}
      loading={createLoading}
      success={success}
      userData={userData}
    />
  );
};

export default RegisterPage;
